import { useKeycloak } from '@react-keycloak/web';
import classNames from 'classnames';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ChevronRightSvg from 'images/v5/icons/chevron-right.svg';
import LogoutIcon from 'images/v5/icons/log-out-03.svg';
import MoonSvg from 'images/v5/icons/moon-star.svg';
import SettingsSvg from 'images/v5/icons/settings-01.svg';
import TranslationSvg from 'images/v5/icons/translate-02.svg';
import UserSvg from 'images/v5/icons/user-02.svg';
import Avatar from 'layout/atoms/Avatar/Avatar';
import Button from 'layout/atoms/Button/Button';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Separator from 'layout/atoms/Separator';
import Switch from 'layout/atoms/Switch/Switch';
import { selectLayout, setTheme } from 'store/layoutSlice';
import { logout, selectUserProfileDataData } from 'store/userSlice';
import { clearOrganizationState } from 'utils/activeOrganization';
import env from 'utils/env';
import styles from './DropdownUser.module.scss';
function DropdownUser() {
    const { t, i18n } = useTranslation(['LAYOUT']);
    const dispatch = useDispatch();
    const { keycloak } = useKeycloak();
    const layoutState = useSelector(selectLayout);
    const userProfileData = useSelector(selectUserProfileDataData);
    const languages = [
        {
            text: 'Pl',
            value: 'pl',
        },
        {
            text: 'En',
            value: 'en',
        },
    ];
    function getLanguage(value) {
        return languages.find((lng) => lng.value === value);
    }
    const [language, setLanguage] = useState(getLanguage(localStorage.getItem('language') || 'pl'));
    /**
     * Signs the user out of this app and of Keycloak.
     *
     * The cleanup runs BEFORE keycloak.logout(), not in its `.then()`. logout() calls
     * window.location.replace() synchronously, so the page is already navigating away and a
     * callback attached to it never runs - the tokens survived in localStorage.
     *
     * That mattered beyond untidy storage: the same shape on the no-organization screen let
     * Keycloak's SSO session outlive the local sign-out, so the next sign-in silently reused
     * the old session and returned a stale token.
     */
    const logOut = () => {
        try {
            localStorage.removeItem('idToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('token');
            localStorage.removeItem('userId');
            localStorage.removeItem('userAvatarSrc');
            sessionStorage.removeItem('token');
            // The active organization and its cached counters/filters must not outlive the session.
            clearOrganizationState();
        }
        catch (_a) {
            // Storage can throw (private mode, blocked site data). The Keycloak logout below is what
            // actually ends the session, so this must not stop it.
        }
        dispatch(logout());
        keycloak.logout({ redirectUri: `${window.location.origin}/` });
    };
    const toggleDarkMode = () => {
        const next = layoutState.theme === 'light' ? 'dark' : 'light';
        dispatch(setTheme(next));
        localStorage.setItem('theme', next);
    };
    const handleLanguageChange = (value) => {
        i18n.changeLanguage(value, () => {
            localStorage.setItem('language', value);
            setLanguage(getLanguage(value));
            moment.updateLocale(value, {
                relativeTime: {},
            });
        });
    };
    return (React.createElement(Dropdown, { size: "m", position: "menubar", closeOnClick: false, trigger: React.createElement("button", { type: "button", className: classNames(styles.wrap, styles.clickable, {
                [`${styles.full}`]: layoutState.showMenu,
            }) },
            React.createElement(Avatar, { size: "m", src: (userProfileData === null || userProfileData === void 0 ? void 0 : userProfileData.avatar) && `${env.static}/${userProfileData === null || userProfileData === void 0 ? void 0 : userProfileData.avatar}`, userName: userProfileData.username }),
            React.createElement("div", { className: classNames(styles.userName, {
                    [`${styles.full}`]: layoutState.showMenu,
                }) },
                React.createElement("p", null, userProfileData.username || '-'),
                React.createElement("span", null, userProfileData.email || '-')),
            React.createElement("span", { className: classNames(styles.chevron, {
                    [`${styles.full}`]: layoutState.showMenu,
                }) },
                React.createElement(ChevronRightSvg, null))) }, (close) => (React.createElement(React.Fragment, null,
        React.createElement("div", { className: classNames(styles.wrap, styles.full) },
            React.createElement(Avatar, { size: "m", src: (userProfileData === null || userProfileData === void 0 ? void 0 : userProfileData.avatar) && `${env.static}/${userProfileData === null || userProfileData === void 0 ? void 0 : userProfileData.avatar}`, userName: userProfileData.username }),
            React.createElement("div", { className: classNames(styles.userName, styles.full) },
                React.createElement("p", null, userProfileData.username || '-'),
                React.createElement("span", null, userProfileData.email || '-'))),
        React.createElement("hr", null),
        React.createElement(Button, { align: "left", shape: "inline", theme: "ghost", leftIcon: React.createElement(UserSvg, null), to: "/user-profile/", onClick: close }, t('SIDEBAR_MENU_PROFILE')),
        React.createElement(Button, { align: "left", shape: "inline", theme: "ghost", leftIcon: React.createElement(SettingsSvg, null), to: "/specification-parameters/", onClick: close }, t('SIDEBAR_MENU_ADMINISTRATION')),
        React.createElement(Separator, { dropdown: true }),
        React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(MoonSvg, null), rightIcon: React.createElement(Switch, { value: layoutState.theme === 'dark', onChange: toggleDarkMode }), onClick: toggleDarkMode }, t('SIDEBAR_MENU_DARK_MODE')),
        language && language.value === 'pl' ? (React.createElement(Button, { align: "left", shape: "inline", theme: "ghost", leftIcon: React.createElement(TranslationSvg, null), onClick: () => handleLanguageChange('en') }, "English")) : (React.createElement(Button, { align: "left", shape: "inline", theme: "ghost", leftIcon: React.createElement(TranslationSvg, null), onClick: () => handleLanguageChange('pl') }, "Polski")),
        React.createElement(Separator, { dropdown: true }),
        React.createElement(Button, { align: "left", shape: "inline", theme: "ghost", color: "danger", leftIcon: React.createElement(LogoutIcon, null), onClick: () => {
                close();
                logOut();
            } }, t('TOPBAR_LOGOUT'))))));
}
export default DropdownUser;
